import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Typography,
  useTheme,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CalendarMonth } from "@mui/icons-material";
import DrawerHeader, { DRAWER_NO_SLIDE, DRAWER_PAPER_PROPS } from "./DrawerHeader";
import InstanceFilter from "./InstanceFilter";

const DATE_OPTIONS = { year: "numeric", month: "long" };

// sx figes au niveau module : recrees a chaque render, ils forcaient Emotion a
// reserialiser la rangee et chaque colonne de trimestre pendant l'ouverture.
const QUARTERS_ROW_SX = {
  display: "flex",
  gap: 2,
  flexWrap: "nowrap",
  overflowX: "auto",
  pb: 1,
};

const QUARTER_COLUMN_SX = (theme) => ({
  minWidth: 250,
  flex: 1,
  display: "flex",
  flexDirection: "column",
  mb: 2,
  position: "relative",
  px: 1,
  "&:not(:last-child)::after": {
    content: '""',
    position: "absolute",
    top: theme.spacing(1),
    bottom: theme.spacing(1),
    width: "2px",
    backgroundColor: theme.palette.divider,
    right: `-${theme.spacing(1)}`,
  },
});

const FILTER_SX = { borderRadius: 2 };

function computeMaintenanceScheduleForCurrentYear(nextMaintenance) {

  if (!nextMaintenance) return [];
  const schedule = [];
  let date = new Date(nextMaintenance);
    for(let i=0; i<2; i++)
    {
    schedule.push(new Date(date));
    date.setMonth(date.getMonth() + 6);
  }
  return schedule;
}

const NextMaintenanceButton = ({ data, role }) => {
  const muiTheme = useTheme();
  const isOnet = role === "onet";
  const isCompass = role === "compass";
  const isDark = muiTheme.palette.mode === "dark";
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prev) => !prev);
    // on attend avant de reset le filtre pour éviter que le Drawer stutter
    setTimeout(function(){
        setFilterInstance("all");
    },1000)
  };

  const [filterInstance, setFilterInstance] = useState("all");
  

  const handleFilterInstanceChange = (instanceName) => {
    setFilterInstance(instanceName);
  };


  // Tout le pipeline (filtrage par rôle, construction des évènements, tris, regroupement
  // par trimestre) tournait dans le corps du rendu. Ce composant vit dans bodyComponent,
  // donc il rejouait tout à chaque render du dashboard, y compris dans la frame où le
  // Drawer démarre son animation. `label` est précalculé ici : toLocaleDateString passe
  // par Intl et coûtait un appel par évènement pendant l'ouverture.
  const { filteredData, groups } = useMemo(() => {
    const source = Array.isArray(data) ? data : [];

    const matchesRole = (item, needle) =>
      ((item.content.name || "").toLowerCase().includes(needle) ||
        (item.content["selected-service"] || "").toLowerCase().includes(needle)) &&
      (item.content.environnement === "PRD" || item.content.environnement === "REC");

    const filteredData = isOnet
      ? source.filter((item) => matchesRole(item, "onet"))
      : isCompass
        ? source.filter((item) => matchesRole(item, "compass"))
        : source;

    const filteredWithEnv = filteredData.filter(
      (item) =>
        filterInstance === "all" ||
        item.content.environnement === filterInstance
    );

    // Un seul formateur réutilisé, au lieu d'un toLocaleDateString par évènement.
    const formatter = new Intl.DateTimeFormat(
      undefined,
      isOnet || isCompass ? DATE_OPTIONS : undefined
    );

    const nowTime = Date.now();

    const events = [];
    filteredWithEnv.forEach((item) => {
      computeMaintenanceScheduleForCurrentYear(
        item.content.next_maintenance
      ).forEach((date) => {
        const diffDays = (date.getTime() - nowTime) / (1000 * 3600 * 24);
        events.push({
          site: item.content.name,
          date,
          label: formatter.format(date),
          isAlert: diffDays <= 7 && diffDays >= 0,
          siteUrl: item.content.siteUrl,
          environnement: item.content.environnement,
        });
      });
    });

    events.sort((a, b) => a.date.getTime() - b.date.getTime());

    // reduce plutot que Math.min(...tableau) : le spread d'un grand tableau est couteux
    // et peut saturer la pile d'appels.
    const now = new Date(nowTime);
    let minYear = now.getFullYear();
    let maxTime = nowTime;
    events.forEach((e) => {
      const t = e.date.getTime();
      if (e.date.getFullYear() < minYear) minYear = e.date.getFullYear();
      if (t > maxTime) maxTime = t;
    });

    const endDate = new Date(maxTime);
    endDate.setFullYear(endDate.getFullYear() + 1);
    const endYear = endDate.getFullYear();
    const endQuarter = Math.floor(endDate.getMonth() / 3) + 1;

    const groups = {};
    for (let y = minYear; y <= endYear; y++) {
      for (let q = 1; q <= 4; q++) {
        if (y === endYear && q > endQuarter) break;
        groups[`${y}-Q${q}`] = [];
      }
    }

    // events est deja trie par date : chaque groupe le reste, plus besoin de retrier.
    events.forEach((ev) => {
      const key = `${ev.date.getFullYear()}-Q${
        Math.floor(ev.date.getMonth() / 3) + 1
      }`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(ev);
    });

    return { filteredData, groups };
  }, [data, isOnet, isCompass, filterInstance]);

  // Trimestres non vides, calcules une fois plutot qu'a chaque render.
  const quarters = useMemo(
    () => Object.keys(groups).filter((q) => groups[q].length > 0),
    [groups]
  );

  return (
      <Box sx={{ my: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleToggle}
          style={{
            width:"372px",
            backgroundColor: isOnet ? "#06305C" : isCompass ? "#C4AC66" : undefined,
            color: isOnet ? "white" : isCompass ? "white" : undefined,
          }}
          sx={{ textTransform: "none", fontWeight: "bold",  borderRadius: 2 }}
        >
          <CalendarMonth></CalendarMonth> Calendrier annuel des maintenances
        </Button>
        <Drawer
          anchor="right"
          open={open}
          onClose={handleToggle}
          PaperProps={DRAWER_PAPER_PROPS.fluid}
          SlideProps={DRAWER_NO_SLIDE}
          // Sans ça, le scroll lock de MUI pose overflow:hidden + padding-right sur
          // <body> à l'ouverture, ce qui relayoute tout le dashboard pendant l'animation.
          disableScrollLock
        >
          <div>
            <DrawerHeader
              title="Calendrier des maintenances"
              actions={
                <InstanceFilter
                  data={filteredData}
                  onFilterChange={handleFilterInstanceChange}
                  role={role}
                  sx={FILTER_SX}
                />
              }
              onClose={handleToggle}
              role={role}
            />

            <div style={{ padding: "20px" }}>
              {/* nowrap : les trimestres restent alignés sur une ligne et c'est ce
                  conteneur qui défile, plutôt que de voir les derniers retomber en
                  dessous. pb réserve la place de l'ascenseur sous les cartes. */}
              <Box sx={QUARTERS_ROW_SX}>
                {quarters.map((quarter) => (
                    <Box
                      key={quarter}
                      sx={QUARTER_COLUMN_SX}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1,
                          textAlign: "center",
                          color: isOnet
                            ? isDark ? "white" : "#06305C"
                            : isCompass
                              ? "#C4AC66"
                              : isDark
                                ? muiTheme.palette.text.primary
                                : undefined
                        }}
                      >
                        {quarter}
                      </Typography>

                      {groups[quarter].map((event, index) => (
                        <Box
                            key={index}
                            sx={{
                              p: 1,
                              mb: 1,
                              backgroundColor: index % 2 === 0
                                ? muiTheme.palette.background.alternate
                                : muiTheme.palette.background.paper,
                              borderLeft: event.isAlert ? "4px solid red" : undefined,
                              borderRadius: "4px",
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ color: muiTheme.palette.text.primary }}>
                              {event.site}
                            </Typography>
                            <Typography variant="body2" sx={{ color: muiTheme.palette.text.primary }}>
                              {event.label}
                            </Typography>
                            {event.siteUrl && (
                              <Typography variant="body2">
                                <a
                                  href={event.siteUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: "none", color: "#1976d2"}}
                                >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <OpenInNewIcon fontSize="small" />
                                  <Typography variant="body2">{event.siteUrl}</Typography>
                                </Box>
                              </a>
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ color: muiTheme.palette.text.primary }}>
                              {event.environnement || "Non défini"}
                            </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
              </Box>
            </div>
          </div>
        </Drawer>
      </Box>
  );
};

export default NextMaintenanceButton;
